import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import AppLayout from "@/components/AppLayout";
import { UrgentRequestBanner } from "@/components/UrgentRequestBanner";
import api, { apiError } from "@/lib/api";
import { OPP_STAGES, OPP_SOURCES, labelFor } from "@/lib/constants";
import CreateRFQDialog from "@/pages/CreateRFQDialog";
import { LinkPickerDialog } from "@/components/LinkPickerDialog";
import TargetClients from "@/pages/TargetClients";
import IndustrySelect, { useIndustries } from "@/components/IndustrySelect";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { OppPeek } from "@/components/OppPeek";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Loader2, Target, GripVertical, User, Phone, ArrowRight, Check, LayoutGrid, Table2, Search, FileText, Search as SearchIcon, ClipboardList, Ship, DollarSign, Clock, Send, Trophy, Link2, X } from "lucide-react";
import { toast } from "sonner";

const mc = (tzs, cur, rate) => (cur === "USD" ? `USD ${Math.round(Number(tzs || 0) / (rate || 1)).toLocaleString()}` : `TZS ${Math.round(Number(tzs || 0)).toLocaleString()}`);
const OPP_KPIS = [
  { key: "total", label: "Total Opportunities", icon: ClipboardList, tint: "text-[#1B4383] bg-[#E6ECF5]", value: (s) => s.total, sub: (s) => `${s.open} Open` },
  { key: "pipeline", label: "Total Pipeline", icon: DollarSign, tint: "text-emerald-600 bg-emerald-50", money: true, value: (s, c, r) => mc(s.pipeline_tzs, c, r), sub: (s, c, r) => `${mc(s.won_tzs, c, r)} Won` },
  { key: "awaiting", label: "Awaiting Decision", icon: Clock, tint: "text-rose-600 bg-rose-50", value: (s) => s.awaiting, sub: () => "Client Deciding" },
  { key: "quoting", label: "In Progress", icon: Send, tint: "text-sky-600 bg-sky-50", value: (s) => s.quoting, sub: (s) => `${s.awaiting} Awaiting Decision` },
  { key: "won", label: "Won (Closed)", icon: Trophy, tint: "text-amber-600 bg-amber-50", money: true, value: (s, c, r) => mc(s.won_tzs, c, r), sub: (s) => `${s.won} Won · ${s.lost} Lost` },
];

const COLUMN_ACCENT = {
  new: "border-t-[#8A94A6]",
  quoting: "border-t-[#1B4383]",
  awaiting_decision: "border-t-[#A87F31]",
  won: "border-t-[#527D63]",
  lost: "border-t-[#9D4848]",
};
const STAGE_STYLE = {
  new: "bg-[#F1F3F6] text-[#687585]",
  quoting: "bg-[#E6ECF5] text-[#1B4383]",
  awaiting_decision: "bg-[#F6EDD9] text-[#A87F31]",
  won: "bg-[#DCEBE1] text-[#2F6B45]",
  lost: "bg-[#F4E7E7] text-[#9D4848]",
};
const SOURCE_STYLE = "bg-[#F1F3F6] text-[#687585] border-[#DDE1E8]";
const empty = { title: "", industry: "", client_id: "", contact_name: "", contact_phone: "", contact_email: "", description: "", estimated_value_tzs: "", stage: "new", source: "field", assigned_to_id: "" };
const fmtTzs = (v) => `TZS ${Number(v || 0).toLocaleString()}`;
const COL_LIMIT = 5;
const SORTERS = {
  newest: (a, b) => (b.created_at || "").localeCompare(a.created_at || ""),
  oldest: (a, b) => (a.created_at || "").localeCompare(b.created_at || ""),
  value: (a, b) => Number(b.estimated_value_tzs || 0) - Number(a.estimated_value_tzs || 0),
};
const growArea = (el) => { if (el) { el.style.height = "auto"; el.style.height = `${Math.max(el.scrollHeight + 2, 200)}px`; } };

export default function Opportunities() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { industries } = useIndustries();
  const indMap = useMemo(() => Object.fromEntries(industries.map((i) => [i.value, i.label])), [industries]);
  const [opps, setOpps] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("kanban");
  const [search, setSearch] = useState("");
  const [fIndustry, setFIndustry] = useState("all");
  const [fOwner, setFOwner] = useState("all");
  const [dialog, setDialog] = useState(null);
  const [saving, setSaving] = useState(false);
  const [addingClient, setAddingClient] = useState(false);
  const [newClient, setNewClient] = useState({ name: "", industry: "mining" });
  const [team, setTeam] = useState([]);
  const [reqq, setReqq] = useState(null);
  const [linkOpp, setLinkOpp] = useState(null);
  const unlinkRfq = async (opp) => {
    if (!window.confirm(`Unlink ${opp.linked_rfq_code || "this RFQ"} from this opportunity?`)) return;
    try { await api.delete(`/opportunities/${opp.id}/link-rfq`); toast.success("RFQ unlinked"); load(); } catch (e) { toast.error(apiError(e)); }
  };
  const [draggingId, setDraggingId] = useState(null);
  const [dragOverCol, setDragOverCol] = useState(null);
  const [rfqOpen, setRfqOpen] = useState(false);
  const [convert, setConvert] = useState(null);
  const [stats, setStats] = useState(null);
  const [currency, setCurrency] = useState("USD");
  const [tab, setTab] = useState("pipeline");
  const [sortBy, setSortBy] = useState("newest");
  const [expanded, setExpanded] = useState({});
  const [peek, setPeek] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/opportunities").then((r) => setOpps(r.data)).catch((e) => toast.error(apiError(e))).finally(() => setLoading(false));
    api.get("/opportunities/summary").then((r) => setStats(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); api.get("/clients").then((r) => setClients(r.data)).catch(() => {}); api.get("/team-members").then((r) => setTeam(r.data)).catch(() => {}); }, [load]);

  useEffect(() => {
    const q = searchParams.get("q");
    if (q) { setSearch(q); setView("table"); }
  }, [searchParams]);
  const openId = searchParams.get("open");
  useEffect(() => {
    const o = openId && opps.find((x) => x.id === openId);
    if (o) { setPeek(o); api.post(`/client-requests/${o.id}/seen`).then(() => window.dispatchEvent(new Event("somcore:alerts"))).catch(() => {}); }
  }, [openId, opps]);

  const owners = useMemo(() => {
    const m = new Map();
    opps.forEach((o) => { if (o.owner_email) m.set(o.owner_email, o.owner_name || o.owner_email); });
    return [...m.entries()].map(([email, name]) => ({ email, name }));
  }, [opps]);

  const filtered = useMemo(() => opps.filter((o) => {
    if (search.trim()) {
      const q = search.toLowerCase();
      if (!`${o.code} ${o.title} ${o.client_name}`.toLowerCase().includes(q)) return false;
    }
    if (fIndustry !== "all" && o.industry !== fIndustry) return false;
    if (fOwner !== "all" && o.owner_email !== fOwner) return false;
    return true;
  }), [opps, search, fIndustry, fOwner]);

  const openNew = (stage = "new") => { setAddingClient(false); setNewClient({ name: "", industry: "mining" }); setDialog({ ...empty, stage, contacts: [{ name: "", phone: "", email: "" }], date_visited: "" }); };
  const openEdit = (o) => {
    setAddingClient(false); setNewClient({ name: "", industry: "mining" });
    setDialog({
      id: o.id, title: o.title, industry: o.industry || "", client_id: o.client_id || "", contact_name: o.contact_name,
      contact_phone: o.contact_phone, contact_email: o.contact_email, description: o.description,
      estimated_value_tzs: o.estimated_value_tzs || "", stage: o.stage, source: o.source, assigned_to_id: o.assigned_to_id || "",
      contacts: (o.contacts && o.contacts.length) ? o.contacts.map((c) => ({ name: c.name || "", phone: c.phone || "", email: c.email || "" })) : [{ name: o.contact_name || "", phone: o.contact_phone || "", email: o.contact_email || "" }],
      date_visited: o.date_visited || "",
    });
  };
  const titleCase = (s) => (s || "").replace(/\b\w/g, (c) => c.toUpperCase());
  const updateContact = (i, k, v) => setDialog((d) => ({ ...d, contacts: d.contacts.map((c, idx) => (idx === i ? { ...c, [k]: k === "name" ? titleCase(v) : v } : c)) }));
  const addContact = () => setDialog((d) => ({ ...d, contacts: [...(d.contacts || []), { name: "", phone: "", email: "" }] }));
  const removeContact = (i) => setDialog((d) => ({ ...d, contacts: d.contacts.filter((_, idx) => idx !== i) }));

  const pickClient = (v) => {
    const c = clients.find((x) => x.id === v);
    setDialog((d) => ({ ...d, client_id: v, industry: c?.industry || d.industry }));
  };

  const addClient = async () => {
    if (!newClient.name.trim()) { toast.error("Enter the client name."); return; }
    try {
      const { data } = await api.post("/clients", { name: newClient.name.trim(), industry: newClient.industry });
      setClients((prev) => [...prev, data].sort((a, b) => a.name.localeCompare(b.name)));
      setDialog((d) => ({ ...d, client_id: data.id, industry: newClient.industry }));
      setAddingClient(false); setNewClient({ name: "", industry: "mining" });
      toast.success("Client added");
    } catch (e) { toast.error(apiError(e)); }
  };

  const save = async () => {
    if (!dialog.title.trim()) { toast.error("Give this opportunity a title."); return; }
    if (!dialog.client_id) { toast.error("Select an existing client or add a new one."); return; }
    setSaving(true);
    const client = clients.find((c) => c.id === dialog.client_id);
    const body = {
      title: dialog.title.trim(),
      industry: dialog.industry || client?.industry || "",
      client_id: dialog.client_id,
      client_name: client?.name || "",
      contact_name: dialog.contacts?.[0]?.name || "",
      contact_phone: dialog.contacts?.[0]?.phone || "",
      contact_email: dialog.contacts?.[0]?.email || "",
      contacts: (dialog.contacts || []).filter((c) => c.name || c.phone || c.email),
      date_visited: dialog.date_visited || "",
      description: dialog.description || "",
      estimated_value_tzs: Number(dialog.estimated_value_tzs) || 0,
      stage: dialog.stage,
      source: dialog.source,
      assigned_to_id: dialog.assigned_to_id || "",
      assigned_to_name: (team.find((t) => t.id === dialog.assigned_to_id) || {}).name || "",
    };
    try {
      if (dialog.id) await api.put(`/opportunities/${dialog.id}`, body);
      else await api.post("/opportunities", body);
      toast.success(dialog.id ? "Opportunity updated" : "Opportunity logged");
      setDialog(null); load();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const move = async (opp, stage) => {
    setOpps((prev) => prev.map((o) => (o.id === opp.id ? { ...o, stage } : o)));
    try { await api.put(`/opportunities/${opp.id}/move`, { stage }); }
    catch (e) { toast.error(apiError(e)); load(); }
  };

  const requestQuote = (opp) => setReqq({ opp, itemsText: "", note: "", assigned_to_id: "" });
  const submitReqq = async () => {
    const items = (reqq.itemsText || "").split("\n").map((l) => l.trim()).filter(Boolean).map((l) => ({ description: l }));
    if (!items.length) { toast.error("Enter at least one item."); return; }
    setSaving(true);
    try {
      const { data } = await api.post(`/opportunities/${reqq.opp.id}/request-quote`, { items, note: reqq.note || "", assigned_to_id: reqq.assigned_to_id || "", assigned_to_name: (team.find((t) => t.id === reqq.assigned_to_id) || {}).name || "" });
      toast.success(`Quote requested — RFQ ${data.rfq_code} created, Sales notified`);
      setReqq(null); load();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const remove = async (opp) => {
    if (!window.confirm(`Delete "${opp.title}"?`)) return;
    try { await api.delete(`/opportunities/${opp.id}`); setOpps((p) => p.filter((o) => o.id !== opp.id)); toast.success("Opportunity deleted"); }
    catch (e) { toast.error(apiError(e)); }
  };

  const onCardClick = (opp) => { setPeek(opp); if (opp.client_request && !opp.client_request.seen) api.post(`/client-requests/${opp.id}/seen`).then(() => { opp.client_request.seen = true; window.dispatchEvent(new Event("somcore:alerts")); }).catch(() => {}); };
  const onRfqCreated = async (rfq) => {
    if (!convert) return;
    try { await api.put(`/opportunities/${convert.id}/link-rfq`, { rfq_id: rfq.id, rfq_code: rfq.code || "" }); } catch (_) { /* best-effort */ }
  };

  const byStage = (s) => filtered.filter((o) => o.stage === s).sort(SORTERS[sortBy]);
  const totalValue = (list) => list.reduce((sum, o) => sum + Number(o.estimated_value_tzs || 0), 0);
  const indLabel = (v) => indMap[v] || labelFor(v, [{ value: "mining", label: "Mining" }, { value: "oil_and_gas", label: "Oil & Gas" }]);

  const filterBar = (
    <div className="mb-4 flex flex-col gap-3 rounded-lg border border-border bg-white p-3 sm:flex-row sm:items-center">
      <div className="relative flex-1">
        <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <Input className="pl-9" placeholder="Search opp #, title or customer..." value={search} onChange={(e) => setSearch(e.target.value)} data-testid="opp-search" />
      </div>
      <Select value={fIndustry} onValueChange={setFIndustry}>
        <SelectTrigger className="sm:w-48" data-testid="opp-filter-industry"><SelectValue placeholder="All Industries" /></SelectTrigger>
        <SelectContent><SelectItem value="all">All Industries</SelectItem>{industries.map((i) => <SelectItem key={i.value} value={i.value}>{i.label}</SelectItem>)}</SelectContent>
      </Select>
      <Select value={fOwner} onValueChange={setFOwner}>
        <SelectTrigger className="sm:w-48" data-testid="opp-filter-owner"><SelectValue placeholder="All Owners" /></SelectTrigger>
        <SelectContent><SelectItem value="all">All Owners</SelectItem>{owners.map((o) => <SelectItem key={o.email} value={o.email}>{o.name}</SelectItem>)}</SelectContent>
      </Select>
      <Select value={sortBy} onValueChange={setSortBy}>
        <SelectTrigger className="sm:w-44" data-testid="opp-sort"><SelectValue /></SelectTrigger>
        <SelectContent><SelectItem value="newest">Newest first</SelectItem><SelectItem value="value">Highest value</SelectItem><SelectItem value="oldest">Oldest first</SelectItem></SelectContent>
      </Select>
      <div className="inline-flex shrink-0 overflow-hidden rounded-md border border-border sm:ml-auto">
        <button onClick={() => setView("kanban")} className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold transition-colors ${view === "kanban" ? "bg-[#12294B] text-white" : "text-slate-500 hover:bg-slate-100"}`} data-testid="opp-view-kanban"><LayoutGrid className="h-3.5 w-3.5" />Kanban</button>
        <button onClick={() => setView("table")} className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold transition-colors ${view === "table" ? "bg-[#12294B] text-white" : "text-slate-500 hover:bg-slate-100"}`} data-testid="opp-view-table"><Table2 className="h-3.5 w-3.5" />Table</button>
      </div>
    </div>
  );

  return (
    <AppLayout title="Opportunities" actions={
      tab === "pipeline" ? <Button onClick={() => openNew()} data-testid="new-opportunity-button"><Plus className="mr-2 h-4 w-4" />Log Opportunity</Button> : null
    }>
      <UrgentRequestBanner key={openId || "none"} />
      <div className="mb-4 inline-flex rounded-lg border border-border bg-white p-1" data-testid="opp-tabs">
        <button onClick={() => setTab("pipeline")} className={`rounded-md px-4 py-1.5 text-sm font-semibold transition-colors ${tab === "pipeline" ? "bg-[#12294B] text-white" : "text-slate-600 hover:bg-slate-50"}`} data-testid="opp-tab-pipeline">Pipeline</button>
        <button onClick={() => setTab("targets")} className={`rounded-md px-4 py-1.5 text-sm font-semibold transition-colors ${tab === "targets" ? "bg-[#12294B] text-white" : "text-slate-600 hover:bg-slate-50"}`} data-testid="opp-tab-targets">Target Clients</button>
      </div>
      {tab === "targets" && <TargetClients />}
      {tab === "pipeline" && (<>
      <div className="mb-3 flex items-center justify-end gap-2">
        <span className="text-xs font-medium text-muted-foreground">Currency</span>
        <div className="flex rounded-md border border-border p-0.5" data-testid="opp-currency-toggle">
          {["USD", "TZS"].map((c) => (
            <button key={c} onClick={() => setCurrency(c)} data-testid={`opp-cur-${c}`}
              className={`rounded px-2.5 py-1 text-xs font-medium ${currency === c ? "bg-brand text-white" : "text-muted-foreground hover:text-slate-900"}`}>{c}</button>
          ))}
        </div>
      </div>
      <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-5" data-testid="opp-kpis">
        {OPP_KPIS.map((k, idx) => {
          const v = stats ? k.value(stats, currency, stats.usd_rate) : "—";
          const lastOdd = idx === OPP_KPIS.length - 1 && OPP_KPIS.length % 2 === 1;
          return (
            <div key={k.key} className={`rounded-lg border border-border bg-white p-4 ${lastOdd ? "col-span-2 lg:col-span-1" : ""}`} data-testid={`opp-kpi-${k.key}`}>
              <div className="mb-2 flex items-center justify-between">
                <span className="text-[11px] font-semibold tracking-wide text-slate-400">{k.label}</span>
                <span className={`flex h-7 w-7 items-center justify-center rounded-md ${k.tint}`}><k.icon className="h-4 w-4" /></span>
              </div>
              <div className={`font-heading font-bold text-slate-900 ${k.money ? "text-lg" : "text-2xl"}`}>{v}</div>
              <div className="mt-0.5 text-xs text-muted-foreground">{stats && k.sub ? k.sub(stats, currency, stats.usd_rate) : "\u00a0"}</div>
            </div>
          );
        })}
      </div>

      {filterBar}

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : view === "kanban" ? (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-5">
          {OPP_STAGES.map((col) => {
            const list = byStage(col.value);
            const open = expanded[col.value];
            const shown = open ? list : list.slice(0, COL_LIMIT);
            return (
              <div key={col.value}
                onDragOver={(e) => { e.preventDefault(); if (dragOverCol !== col.value) setDragOverCol(col.value); }}
                onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setDragOverCol(null); }}
                onDrop={(e) => { e.preventDefault(); const opp = opps.find((o) => o.id === draggingId); if (opp && opp.stage !== col.value) move(opp, col.value); setDragOverCol(null); setDraggingId(null); }}
                className={`flex flex-col rounded-lg border border-t-4 border-border bg-slate-50/60 transition-shadow ${COLUMN_ACCENT[col.value]} ${dragOverCol === col.value ? "ring-2 ring-[#1B4383] ring-offset-1" : ""}`} data-testid={`opp-col-${col.value}`}>
                <div className="px-4 py-3">
                  <div className="flex items-center justify-between gap-2">
                    <h3 className="font-heading text-sm font-bold tracking-tight text-slate-800">{col.label}</h3>
                    <div className="flex items-center gap-1.5">
                      <span className="rounded-full bg-white px-2 py-0.5 text-xs font-medium text-slate-500 border border-border" data-testid={`opp-count-${col.value}`}>{list.length}</span>
                      <button onClick={() => openNew(col.value)} className="flex h-6 w-6 items-center justify-center rounded-md border border-border bg-white text-slate-500 transition-colors hover:border-[#1B4383] hover:text-[#1B4383]" title={`Add opportunity to ${col.label}`} data-testid={`opp-col-add-${col.value}`}><Plus className="h-3.5 w-3.5" /></button>
                    </div>
                  </div>
                  {list.length > 0 && <div className="mt-1 text-[11px] font-medium text-slate-400">{fmtTzs(totalValue(list))}</div>}
                </div>
                <div className="max-h-[70vh] space-y-3 overflow-y-auto px-3 pb-3" data-testid={`opp-col-body-${col.value}`}>
                  {shown.map((opp) => (
                    <div key={opp.id} draggable
                      onDragStart={(e) => { setDraggingId(opp.id); e.dataTransfer.effectAllowed = "move"; }}
                      onDragEnd={() => { setDraggingId(null); setDragOverCol(null); }}
                      onClick={() => onCardClick(opp)}
                      role="button"
                      className={`group cursor-pointer rounded-md border border-border bg-white p-3 shadow-sm transition-colors hover:border-[#1B4383]/50 ${draggingId === opp.id ? "opacity-50" : ""}`} data-testid={`opp-card-${opp.id}`}>
                      <div className="mb-1.5 flex items-start justify-between gap-2">
                        <span className="font-mono text-[10px] font-semibold text-slate-400">{opp.code}</span>
                        <div className="flex gap-1 opacity-100 transition-opacity sm:opacity-0 sm:group-hover:opacity-100">
                          <button onClick={(e) => { e.stopPropagation(); openEdit(opp); }} className="text-slate-400 hover:text-primary" data-testid={`opp-edit-${opp.id}`}><Pencil className="h-3.5 w-3.5" /></button>
                          <button onClick={(e) => { e.stopPropagation(); remove(opp); }} className="text-slate-400 hover:text-destructive" data-testid={`opp-delete-${opp.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                        </div>
                      </div>
                      <div className="font-medium text-slate-900">{opp.title}</div>
                      {opp.client_name && <div className="mt-0.5 text-xs font-medium text-[#1B4383]">{opp.client_name}</div>}
                      {opp.description && <p className="mt-1.5 line-clamp-2 whitespace-pre-line text-xs leading-snug text-slate-600" data-testid={`opp-notes-preview-${opp.id}`}>{opp.description}</p>}
                      <div className="mt-1 flex flex-wrap items-center gap-1.5">
                        {opp.industry && <span className="inline-flex items-center rounded-full border border-[#EBD9AE] bg-[#F6EDD9] px-2 py-0.5 text-[10px] font-semibold text-[#A87F31]">{indLabel(opp.industry)}</span>}
                        <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold tracking-wide ${SOURCE_STYLE}`}>{labelFor(opp.source, OPP_SOURCES)}</span>
                      </div>
                      {opp.contact_name && <div className="mt-1.5 flex items-center gap-1 text-xs text-muted-foreground"><User className="h-3 w-3" />{opp.contact_name}{opp.contact_phone ? <span className="ml-1 flex items-center gap-1"><Phone className="h-3 w-3" />{opp.contact_phone}</span> : null}</div>}
                      {Number(opp.estimated_value_tzs) > 0 && <div className="mt-1.5 font-mono text-sm font-semibold text-slate-900">{fmtTzs(opp.estimated_value_tzs)}</div>}
                      {opp.date_visited && <div className="mt-1 text-[11px] text-slate-400">Visited {opp.date_visited}</div>}
                      {opp.linked_rfq_id ? (
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          <button onClick={(e) => { e.stopPropagation(); navigate(`/rfqs/${opp.linked_rfq_id}`); }} className="inline-flex items-center gap-1 rounded-md bg-[#E4EEE8] px-2 py-1 text-[11px] font-semibold text-[#2F6B45] hover:bg-[#d7e7dd]" data-testid={`opp-rfq-link-${opp.id}`}><FileText className="h-3 w-3" />{opp.linked_rfq_code || "View RFQ"}</button>
                          <button onClick={(e) => { e.stopPropagation(); unlinkRfq(opp); }} title="Unlink RFQ" className="inline-flex items-center rounded-md px-1 text-slate-400 hover:text-destructive" data-testid={`opp-unlink-rfq-${opp.id}`}><X className="h-3.5 w-3.5" /></button>
                          {opp.linked_order_id && <button onClick={(e) => { e.stopPropagation(); navigate(`/orders/${opp.linked_order_id}`); }} className="inline-flex items-center gap-1 rounded-md bg-[#E6ECF5] px-2 py-1 text-[11px] font-semibold text-[#1B4383] hover:bg-[#d9e2f0]" data-testid={`opp-order-link-${opp.id}`}><ClipboardList className="h-3 w-3" />{opp.linked_order_no || "Customer PO"}</button>}
                          {opp.linked_shipment_id && <button onClick={(e) => { e.stopPropagation(); navigate(`/logistics/shipment/${opp.linked_shipment_id}`); }} className="inline-flex items-center gap-1 rounded-md bg-[#EAE6F5] px-2 py-1 text-[11px] font-semibold text-[#5B4B8A] hover:bg-[#e0daf0]" data-testid={`opp-shipment-link-${opp.id}`}><Ship className="h-3 w-3" />{opp.linked_shipment_no || "Shipment"}</button>}
                        </div>
                      ) : (
                        <button onClick={(e) => { e.stopPropagation(); requestQuote(opp); }} className="mt-2 inline-flex items-center gap-1 rounded-md border border-[#1B4383]/30 px-2 py-1 text-[11px] font-semibold text-[#1B4383] hover:bg-[#E6ECF5]" data-testid={`opp-request-quote-${opp.id}`}><Send className="h-3 w-3" />Request Quote</button>
                      )}
                      {!opp.linked_rfq_id && <button onClick={(e) => { e.stopPropagation(); setLinkOpp(opp); }} className="ml-1.5 mt-2 inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-[11px] font-semibold text-slate-600 hover:bg-slate-50" data-testid={`opp-link-rfq-${opp.id}`}><Link2 className="h-3 w-3" />Link Existing RFQ</button>}
                      {opp.owner_name && <div className="mt-1 text-[11px] text-slate-400">Logged by {opp.owner_name}</div>}
                      <div className="mt-2.5 flex items-center gap-1.5 border-t border-border pt-2" onClick={(e) => e.stopPropagation()}>
                        <GripVertical className="h-3.5 w-3.5 text-slate-300" />
                        <Select value={opp.stage} onValueChange={(v) => move(opp, v)}>
                          <SelectTrigger className="h-7 flex-1 text-xs" data-testid={`opp-move-${opp.id}`}><SelectValue /></SelectTrigger>
                          <SelectContent>{OPP_STAGES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                    </div>
                  ))}
                  {list.length > COL_LIMIT && (
                    <button onClick={() => setExpanded((p) => ({ ...p, [col.value]: !open }))} className="w-full rounded-md border border-border bg-white py-2 text-xs font-semibold text-[#1B4383] hover:bg-[#E6ECF5]" data-testid={`opp-show-more-${col.value}`}>
                      {open ? "Show less" : `Show ${list.length - COL_LIMIT} more`}
                    </button>
                  )}
                  <button onClick={() => openNew(col.value)} className={`flex w-full items-center justify-center gap-1 rounded-md border border-dashed border-slate-300 text-xs text-slate-400 hover:border-primary hover:text-primary ${list.length ? "py-2" : "py-4"}`} data-testid={`opp-add-${col.value}`}>
                    <Plus className="h-3.5 w-3.5" /> Add opportunity
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border bg-white">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                <th className="px-4 py-3 font-semibold">Opp #</th>
                <th className="px-4 py-3 font-semibold">Title</th>
                <th className="px-4 py-3 font-semibold">Industry</th>
                <th className="px-4 py-3 font-semibold">Customer</th>
                <th className="px-4 py-3 font-semibold">Owner</th>
                <th className="px-4 py-3 font-semibold">Stage</th>
                <th className="px-4 py-3 text-right font-semibold">Est. Value</th>
                <th className="px-4 py-3 font-semibold">RFQ</th>
                <th className="w-16 px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {filtered.map((opp) => (
                <tr key={opp.id} onClick={() => onCardClick(opp)} className="group cursor-pointer border-b border-border last:border-0 hover:bg-slate-50" data-testid={`opp-row-${opp.id}`}>
                  <td className="px-4 py-3 font-mono text-xs text-slate-500">{opp.code}</td>
                  <td className="px-4 py-3 font-medium text-slate-900">{opp.title}</td>
                  <td className="px-4 py-3">{opp.industry ? <span className="inline-flex items-center rounded-full border border-[#EBD9AE] bg-[#F6EDD9] px-2 py-0.5 text-[10px] font-semibold text-[#A87F31]">{indLabel(opp.industry)}</span> : <span className="text-slate-300">—</span>}</td>
                  <td className="px-4 py-3 text-slate-700">{opp.client_name || "—"}</td>
                  <td className="px-4 py-3 text-slate-600">{opp.owner_name || "—"}</td>
                  <td className="px-4 py-3"><span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${STAGE_STYLE[opp.stage]}`}>{labelFor(opp.stage, OPP_STAGES)}</span></td>
                  <td className="px-4 py-3 text-right font-mono text-slate-900">{Number(opp.estimated_value_tzs) > 0 ? fmtTzs(opp.estimated_value_tzs) : "—"}</td>
                  <td className="px-4 py-3">{opp.linked_rfq_id ? <button onClick={(e) => { e.stopPropagation(); navigate(`/rfqs/${opp.linked_rfq_id}`); }} className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#2F6B45] hover:underline" data-testid={`opp-row-rfq-${opp.id}`}><FileText className="h-3 w-3" />{opp.linked_rfq_code || "View"}</button> : <button onClick={(e) => { e.stopPropagation(); setLinkOpp(opp); }} className="inline-flex items-center gap-1 text-[11px] font-medium text-[#1B4383] hover:underline" data-testid={`opp-row-link-rfq-${opp.id}`}><Link2 className="h-3 w-3" />Link RFQ</button>}</td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                      <button onClick={(e) => { e.stopPropagation(); openEdit(opp); }} className="text-slate-400 hover:text-primary" data-testid={`opp-row-edit-${opp.id}`}><Pencil className="h-3.5 w-3.5" /></button>
                      <button onClick={(e) => { e.stopPropagation(); remove(opp); }} className="text-slate-400 hover:text-destructive" data-testid={`opp-row-delete-${opp.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {!filtered.length && <tr><td colSpan={9} className="px-4 py-12 text-center text-muted-foreground">No opportunities match your filters.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      <LinkPickerDialog open={!!linkOpp} onClose={() => setLinkOpp(null)} testid="opp-link-rfq-dialog"
        title={`Link Existing RFQ${linkOpp ? ` — ${linkOpp.title}` : ""}`} url={linkOpp ? `/opportunities/${linkOpp.id}/rfq-options` : ""}
        sameLabel={linkOpp?.client_name ? `${linkOpp.client_name} RFQs` : "Same Client"}
        onPick={async (r) => { await api.put(`/opportunities/${linkOpp.id}/link-rfq`, { rfq_id: r.id, rfq_code: r.code }); toast.success(`Linked to ${r.code}`); load(); }} />

      <Dialog open={!!reqq} onOpenChange={(o) => !o && setReqq(null)}>
        <DialogContent data-testid="opp-reqq-dialog">
          <DialogHeader><DialogTitle>Request Quote{reqq?.opp ? ` — ${reqq.opp.title}` : ""}</DialogTitle></DialogHeader>
          {reqq && (
            <div className="space-y-4">
              <div className="space-y-2"><Label>Items (one per line)</Label><Textarea rows={4} value={reqq.itemsText} onChange={(e) => setReqq({ ...reqq, itemsText: e.target.value })} placeholder={"2 x Air filter element\n1 x Hydraulic seal kit"} data-testid="opp-reqq-items" /></div>
              <div className="space-y-2"><Label>Request From</Label>
                <Select value={reqq.assigned_to_id || "unassigned"} onValueChange={(v) => setReqq({ ...reqq, assigned_to_id: v === "unassigned" ? "" : v })}>
                  <SelectTrigger data-testid="opp-reqq-assignee"><SelectValue placeholder="Select a team member" /></SelectTrigger>
                  <SelectContent><SelectItem value="unassigned">Any (notify Sales team)</SelectItem>{team.map((t) => <SelectItem key={t.id} value={t.id}>{t.full_name || t.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label>Note (optional)</Label><Textarea rows={2} value={reqq.note} onChange={(e) => setReqq({ ...reqq, note: e.target.value })} data-testid="opp-reqq-note" /></div>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setReqq(null)}>Cancel</Button><Button onClick={submitReqq} disabled={saving} data-testid="opp-reqq-submit">{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Send Request</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent data-testid="opp-dialog">
          <DialogHeader><DialogTitle>{dialog?.id ? "Edit Opportunity" : "Log Opportunity"}</DialogTitle></DialogHeader>
          {dialog && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Title</Label>
                <Input value={dialog.title} onChange={(e) => setDialog({ ...dialog, title: e.target.value })} placeholder="e.g. Crane spares for Geita mine site" data-testid="opp-title" />
              </div>
              <div className="space-y-2">
                <Label>Assigned To</Label>
                <Select value={dialog.assigned_to_id || "unassigned"} onValueChange={(v) => setDialog({ ...dialog, assigned_to_id: v === "unassigned" ? "" : v })}>
                  <SelectTrigger data-testid="opp-assignee"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                  <SelectContent><SelectItem value="unassigned">Unassigned</SelectItem>{team.map((t) => <SelectItem key={t.id} value={t.id}>{t.full_name || t.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Client</Label>
                {!addingClient ? (
                  <div className="flex gap-2">
                    <Select value={dialog.client_id} onValueChange={pickClient}>
                      <SelectTrigger className="flex-1" data-testid="opp-client-select"><SelectValue placeholder="Select Existing Client" /></SelectTrigger>
                      <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                    </Select>
                    <Button type="button" variant="outline" onClick={() => setAddingClient(true)} data-testid="opp-add-client-toggle"><Plus className="mr-1 h-4 w-4" />New</Button>
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-2 rounded-md border border-dashed border-slate-300 p-2">
                    <Input className="min-w-[10rem] flex-1" placeholder="New client name" value={newClient.name} onChange={(e) => setNewClient({ ...newClient, name: e.target.value })} data-testid="opp-new-client-name" />
                    <IndustrySelect className="w-40" value={newClient.industry} onChange={(v) => setNewClient({ ...newClient, industry: v })} testid="opp-new-client-industry" />
                    <Button type="button" onClick={addClient} data-testid="opp-new-client-save"><Check className="mr-1 h-4 w-4" />Add</Button>
                    <Button type="button" variant="ghost" onClick={() => setAddingClient(false)}>Cancel</Button>
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Industry</Label>
                  <IndustrySelect value={dialog.industry} onChange={(v) => setDialog({ ...dialog, industry: v })} testid="opp-industry" />
                </div>
                <div className="space-y-2">
                  <Label>Estimated Value (TZS)</Label>
                  <Input type="number" value={dialog.estimated_value_tzs} onChange={(e) => setDialog({ ...dialog, estimated_value_tzs: e.target.value })} placeholder="0" data-testid="opp-value" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Source</Label>
                  <Select value={dialog.source} onValueChange={(v) => setDialog({ ...dialog, source: v })}>
                    <SelectTrigger data-testid="opp-source"><SelectValue /></SelectTrigger>
                    <SelectContent>{OPP_SOURCES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Date Visited</Label>
                  <Input type="date" value={dialog.date_visited || ""} onChange={(e) => setDialog({ ...dialog, date_visited: e.target.value })} data-testid="opp-date-visited" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Contacts</Label>
                {(dialog.contacts || []).map((c, i) => (
                  <div key={i} className="space-y-2 rounded-md border border-slate-200 p-3" data-testid={`opp-contact-${i}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold text-slate-500">Contact {i + 1}</span>
                      {dialog.contacts.length > 1 && <button type="button" onClick={() => removeContact(i)} className="text-slate-400 hover:text-destructive" data-testid={`opp-contact-remove-${i}`}><Trash2 className="h-3.5 w-3.5" /></button>}
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <Input placeholder="Contact name" value={c.name || ""} onChange={(e) => updateContact(i, "name", e.target.value)} data-testid={`opp-contact-name-${i}`} />
                      <Input placeholder="+255 ..." value={c.phone || ""} onChange={(e) => updateContact(i, "phone", e.target.value)} data-testid={`opp-contact-phone-${i}`} />
                    </div>
                    <Input placeholder="name@company.com" value={c.email || ""} onChange={(e) => updateContact(i, "email", e.target.value)} data-testid={`opp-contact-email-${i}`} />
                  </div>
                ))}
                <Button type="button" variant="outline" size="sm" onClick={addContact} data-testid="opp-add-contact"><Plus className="mr-1 h-4 w-4" />Add Another Contact</Button>
              </div>
              <div className="space-y-2">
                <Label>Stage</Label>
                <Select value={dialog.stage} onValueChange={(v) => setDialog({ ...dialog, stage: v })}>
                  <SelectTrigger data-testid="opp-stage"><SelectValue /></SelectTrigger>
                  <SelectContent>{OPP_STAGES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea rows={8} ref={growArea} value={dialog.description} onChange={(e) => { setDialog({ ...dialog, description: e.target.value }); growArea(e.target); }} placeholder="What they need, timelines, next steps..." className="min-h-[200px] resize-y text-sm leading-relaxed" data-testid="opp-description" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(null)}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="opp-save">
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{dialog?.id ? "Save Changes" : "Log Opportunity"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <OppPeek opp={peek} onClose={() => setPeek(null)} onEdit={(o) => { setPeek(null); openEdit(o); }}
        stageLabel={peek ? labelFor(peek.stage, OPP_STAGES) : ""} stageStyle={peek ? STAGE_STYLE[peek.stage] : ""}
        sourceLabel={peek ? labelFor(peek.source, OPP_SOURCES) : ""} industryLabel={peek?.industry ? indLabel(peek.industry) : ""} />

      <CreateRFQDialog
        open={rfqOpen}
        onOpenChange={(o) => { setRfqOpen(o); if (!o) setConvert(null); }}
        clients={clients}
        onClientCreated={(c) => setClients((prev) => [...prev, c].sort((a, b) => a.name.localeCompare(b.name)))}
        initialClientId={convert?.client_id || ""}
        initialTitle={convert?.title || ""}
        onCreated={onRfqCreated}
      />
      </>)}
    </AppLayout>
  );
}
